import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Check, Mail, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function OrderSuccess() {
  const { orderId } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [book, setBook] = useState(null);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    let cancelled = false;
    const pollStripe = async (attempts = 0) => {
      if (cancelled || attempts > 6) return;
      try {
        const { data } = await api.get(`/stripe/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const { data: r } = await api.get(`/orders/${orderId}/public`);
          setOrder(r.order); setBook(r.book);
          return;
        }
      } catch {}
      setTimeout(() => pollStripe(attempts + 1), 2000);
    };
    if (sessionId) pollStripe();
    api.get(`/orders/${orderId}/public`).then((r) => {
      setOrder(r.data.order); setBook(r.data.book);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [orderId]);

  return (
    <div className="max-w-[900px] mx-auto px-6 lg:px-12 pt-16 pb-24 text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#2A3B32] text-white" data-testid="order-success-icon">
        <Check size={24} />
      </div>
      <p className="eyebrow mt-8">Order confirmed</p>
      <h1 className="font-serif-display text-5xl sm:text-6xl leading-none tracking-tighter mt-6">
        The book is <em className="italic text-[#B85C38]">on its way</em>.
      </h1>
      {order && (
        <p className="mt-6 text-[#5C5C5C]">
          Order <span className="font-mono">#{order.id.slice(-8)}</span> · <em className="font-serif-display not-italic text-[#1C1C1C]">{order.book_title}</em>
          {order.email && <> · sent to <span className="text-[#1C1C1C]">{order.email}</span></>}
        </p>
      )}

      <div className="mt-14 grid md:grid-cols-2 gap-6 text-left">
        <div className="border border-[#E5E2DC] p-6 bg-white">
          <Mail className="text-[#B85C38]" size={22} />
          <h3 className="font-serif-display text-2xl mt-4">Check your email</h3>
          <p className="text-[#5C5C5C] mt-2 text-sm">We've sent your PDF to the email on file. It may take a minute to arrive.</p>
        </div>
        <div className="border border-[#E5E2DC] p-6 bg-white">
          <BookOpen className="text-[#B85C38]" size={22} />
          <h3 className="font-serif-display text-2xl mt-4">Read anytime</h3>
          <p className="text-[#5C5C5C] mt-2 text-sm">{book?.pdf_url ? "Download it directly below, or from your library." : "Your PDF is on the way to your inbox."}</p>
        </div>
      </div>

      {book?.pdf_url && (
        <div className="mt-10 space-y-4">
          <a href={book.pdf_url} target="_blank" rel="noreferrer" data-testid="direct-download" className="btn-primary">Download the book (PDF)</a>
          <div>
            <p className="eyebrow mb-4">Your 2 free bonuses</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <a href="https://customer-assets.emergentagent.com/job_digital-library-217/artifacts/wragnul1_DISCIPLINE%20tracker.pdf" target="_blank" rel="noreferrer" data-testid="download-tracker" className="btn-ghost" style={{ borderColor: "#B85C38", color: "#B85C38" }}>Discipline Tracker</a>
              <a href="https://customer-assets.emergentagent.com/job_digital-library-217/artifacts/7bgfnf8y_TRANSFORMATION%20guide.pdf" target="_blank" rel="noreferrer" data-testid="download-guide" className="btn-ghost" style={{ borderColor: "#B85C38", color: "#B85C38" }}>Transformation Guide</a>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6">
        {user ? (
          <Link to="/library" data-testid="go-to-library" className="link-underline text-sm">Open my library →</Link>
        ) : (
          <Link to="/catalog" data-testid="back-to-catalog" className="link-underline text-sm">← Back to the catalog</Link>
        )}
      </div>
    </div>
  );
}
